import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, AlertCircle } from 'lucide-react';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import FAQAccordion from '@/components/marketing/FAQAccordion';
import { Button } from '@/components/ui/button';
import { FEATURES, FORMATS } from '@/data/site-content';
import { breadcrumbJsonLd, serviceJsonLd, graph } from '@/lib/jsonld';

export default function FeatureDetail() {
  const { slug } = useParams();
  const f = FEATURES.find((x) => x.slug === slug);
  if (!f) return <Navigate to="/features" replace />;
  const Icon = f.icon;

  const relatedFeatures = (f.relatedFeatures || [])
    .map((s) => FEATURES.find((x) => x.slug === s))
    .filter(Boolean);
  const relatedFormats = (f.relatedFormats || [])
    .map((s) => FORMATS.find((x) => x.slug === s))
    .filter(Boolean);

  const faqJsonLd = Array.isArray(f.faq) && f.faq.length > 0 ? {
    '@type': 'FAQPage',
    mainEntity: f.faq.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  } : null;

  const jsonLdNodes = [
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'Features', path: '/features' },
      { label: f.title },
    ]),
    serviceJsonLd({
      name: f.title,
      description: f.short,
      serviceType: 'Performance traffic platform feature',
      url: `/features/${f.slug}`,
    }),
  ];
  if (faqJsonLd) jsonLdNodes.push(faqJsonLd);

  return (
    <>
      <SEOHead path={`/features/${f.slug}`} jsonLd={graph(...jsonLdNodes)} />
      <PageHeader
        breadcrumbs={[{ label: 'Features', to: '/features' }, { label: f.title }]}
        eyebrow="Feature"
        title={f.title}
        subtitle={f.short}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button asChild className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-xl transition-colors">
            <Link to="/apply" data-testid={`feature-${f.slug}-apply-cta`} className="inline-flex items-center gap-2">Apply as Advertiser <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" className="bg-white/5 text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl transition-colors">
            <Link to="/features">All features</Link>
          </Button>
        </div>
      </PageHeader>

      <section className="bg-[#F5F7FB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
          <aside>
            <div className="rounded-2xl bg-white border border-slate-900/10 p-6 shadow-[0_10px_30px_rgba(2,6,23,0.06)] sticky top-24">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#22D3EE]/10 text-[#0891B2] border border-[#22D3EE]/30">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-4 text-base font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.short}</p>
            </div>
          </aside>
          <div className="lg:col-span-2 prose-dashbound">
            <h2>What it covers</h2>
            <ul>
              {f.points.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
            <h2>How it shows up in your campaign</h2>
            <p>You’ll see this through your account manager, source-level reports, and the launch checklist we share before you spend. We aim for honest, useful guidance — not magic.</p>
            {f.note && (
              <div className="mt-6 rounded-2xl bg-blue-50 border border-blue-200 p-5 not-prose">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 shrink-0" />
                  <p className="text-sm text-blue-900"><strong>What we won’t claim:</strong> {f.note}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {Array.isArray(f.faq) && f.faq.length > 0 && (
        <section className="bg-[#07111F] text-white" data-testid={`feature-${f.slug}-faq-section`}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#22D3EE]">FAQ</span>
            <h2 className="font-display mt-2 text-2xl sm:text-3xl font-bold">Frequently asked questions</h2>
            <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 px-5 backdrop-blur-xl">
              <FAQAccordion items={f.faq} idPrefix={`feat-${f.slug}`} dark />
            </div>
          </div>
        </section>
      )}

      {/* Related internal links */}
      {(relatedFeatures.length > 0 || relatedFormats.length > 0) && (
        <section className="bg-[#F5F7FB]" data-testid={`feature-${f.slug}-related-section`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-10">
            {relatedFeatures.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900">Related features</h2>
                <ul className="mt-4 space-y-2">
                  {relatedFeatures.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/features/${r.slug}`}
                        data-testid={`feature-${f.slug}-related-feature-${r.slug}`}
                        className="group inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#2563EB] transition-colors"
                      >
                        <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                        {r.title}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/apply"
                      data-testid={`feature-${f.slug}-related-apply`}
                      className="group inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#2563EB] transition-colors"
                    >
                      <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                      Apply as Advertiser
                    </Link>
                  </li>
                </ul>
              </div>
            )}
            {relatedFormats.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold text-slate-900">Recommended formats</h2>
                <ul className="mt-4 space-y-2">
                  {relatedFormats.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/ad-formats/${r.slug}`}
                        data-testid={`feature-${f.slug}-related-format-${r.slug}`}
                        className="group inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#2563EB] transition-colors"
                      >
                        <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                        {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Special FAQ + How-it-works links for top-up-model */}
            {f.slug === 'top-up-model' && (
              <div className="md:col-span-2">
                <h2 className="font-display text-xl font-bold text-slate-900">Helpful next steps</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                  <li>
                    <Link
                      to="/apply"
                      data-testid="feature-top-up-model-link-apply"
                      className="group inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#2563EB] transition-colors"
                    >
                      <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                      Apply as Advertiser
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/#home-how-it-works-section"
                      data-testid="feature-top-up-model-link-how"
                      className="group inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#2563EB] transition-colors"
                    >
                      <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                      See how it works
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/faq"
                      data-testid="feature-top-up-model-link-faq"
                      className="group inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-[#2563EB] transition-colors"
                    >
                      <ArrowRight className="h-3.5 w-3.5 text-[#22D3EE] transition-transform group-hover:translate-x-0.5" />
                      Read the advertiser FAQ
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      <CTASection primaryTo="/apply" />
    </>
  );
}
